import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// 🌐 Container & Layout
const PageContainer = styled.div`
  max-width: 1100px;
  margin: 40px auto;
  padding: 20px;
  font-family: "Inter", "Segoe UI", sans-serif;
  color: #1a1a1a;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  padding: 30px;
  margin-top: 20px;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
  }
`;

const Heading = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #10104e;
  text-align: center;
  margin-bottom: 25px;
`;

// 🎛 Toolbar
const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-bottom: 25px;
`;

const Button = styled.button`
  background: ${(props) => (props.secondary ? "#e0e7ff" : "#10104e")};
  color: ${(props) => (props.secondary ? "#10104e" : "white")};
  border: none;
  border-radius: 10px;
  padding: 10px 18px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    background: ${(props) =>
      props.secondary ? "#c7d2fe" : "#1e1eb7"};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// 📊 Table Styles
const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
`;

const TableHead = styled.thead`
  background: #f0f2f8;
`;

const TableHeader = styled.th`
  padding: 14px;
  text-align: center;
  font-size: 15px;
  color: #10104e;
  font-weight: 600;
  border-bottom: 2px solid #e0e7ff;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: #fafbff;
  }
`;

const TableCell = styled.td`
  text-align: center;
  padding: 12px;
  font-size: 15px;
  border-bottom: 1px solid #f0f2f8;

  input {
    width: 80%;
    padding: 6px 8px;
    font-size: 15px;
    border-radius: 8px;
    border: 1px solid #ccc;
    text-align: center;
    outline: none;
    transition: border 0.2s ease;

    &:focus {
      border-color: #10104e;
    }
  }
`;

// ⚙️ Component
export default function UpdateJamaatTimings() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const staticNames = useMemo(
    () => ["Fajr", "Zuhr", "Asr", "Maghrib", "Isha"],
    []
  );

  const [timings, setTimings] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    axios
      .get(`${apiUrl}/jamat-timings`)
      .then((response) => {
        if (typeof response.data === "object") {
          const updatedTimings = staticNames.map((name) => ({
            name,
            time: response.data[name.toLowerCase()] || "Not available",
          }));
          setTimings(updatedTimings);
        }
      })
      .catch((error) => console.error("Error fetching jamat timings:", error));
  }, [staticNames, apiUrl]);

  const handleEditClick = () => setIsEditing(!isEditing);

  const handleConfirmClick = () => {
    const formattedTimings = timings.reduce((acc, timing) => {
      acc[timing.name.toLowerCase()] = timing.time;
      return acc;
    }, {});

    axios
      .put(`${apiUrl}/jamat-timings`, formattedTimings)
      .then(() => setIsEditing(false))
      .catch((error) => console.error("Error updating jamat timings:", error));
  };

  const handleInputChange = (index, value) => {
    const updatedTimings = [...timings];
    updatedTimings[index].time = value;
    setTimings(updatedTimings);
  };

  // Navigation helpers
  const addBayan = (type) =>
    navigate(`/${type === "morning" ? "newbayanmorning" : "newbayan"}`);
  const deletebayan = () => navigate("/update-delete-morningfridaybayan");
  const deleteSundaybayan = () => navigate("/update-delete-morningSundaybayan");
  const newEventModal = () => navigate("/new-event-modal");
  const allevents = () => navigate("/all-events-flagged");

  return (
    <PageContainer>
      <Heading>🕌 Jamat Timings Dashboard</Heading>

      <Card>
        <Toolbar>
          <Button onClick={handleEditClick}>
            {isEditing ? "Cancel Edit" : "Edit Timings"}
          </Button>
          <Button onClick={handleConfirmClick} disabled={!isEditing}>
            Save Changes
          </Button>
          <Button secondary onClick={() => addBayan("friday")}>
            + Friday Bayan
          </Button>
          <Button secondary onClick={() => addBayan("morning")}>
            + Morning Dars
          </Button>
        </Toolbar>

        <TableWrapper>
          <Table>
            <TableHead>
              <TableRow>
                {staticNames.map((name, index) => (
                  <TableHeader key={index}>{name}</TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <tbody>
              <TableRow>
                {timings.map((timing, index) => (
                  <TableCell key={index}>
                    {isEditing ? (
                      <input
                        type="text"
                        value={timing.time}
                        onChange={(e) =>
                          handleInputChange(index, e.target.value)
                        }
                      />
                    ) : (
                      timing.time
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </tbody>
          </Table>
        </TableWrapper>
      </Card>

      <Card>
        <Toolbar>
          <Button secondary onClick={deletebayan}>
            🗑 Delete Friday Bayan
          </Button>
          <Button secondary onClick={deleteSundaybayan}>
            🗑 Delete Morning Dars
          </Button>
          <Button secondary onClick={newEventModal}>
            📅 Add New Event
          </Button>
          <Button secondary onClick={allevents}>
            📋 Show All Events
          </Button>
        </Toolbar>
      </Card>
    </PageContainer>
  );
}
